import { useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiPost, apiPut } from '../../services/genesisApi/httpsMethods';
import { CustomButton } from '../../components/common/customButton';
import { ConfirmDialog } from '../../components/common/confirmDialog';
import { SimpleToolbar } from '../../components/layout/Toolbar';
import { DynamicForm } from '../../components/common/DynamicForm';
import { sponsorFieldConfig } from '../../config/sponsorFieldConfig';
import { SPONSOR_TYPE_OPTIONS } from '../../constants/sponsorTypes';
import type { Sponsor } from '../../types/deal';
import { useCurrentUser } from '../../context/CurrentUserContext';

const sponsorTypeFormOptions = [
    { id: '', label: '(None)' },
    ...SPONSOR_TYPE_OPTIONS.map((t) => ({ id: t, label: t })),
];

interface CreateSponsorProps {
    sponsor?: Partial<Sponsor>;
    isEdit?: boolean;
}

const emptySponsor: Partial<Sponsor> = {
    sponsor_name: '',
    sponsor_address: '',
    sponsor_type: '',
    kyc_completed: false,
    short_description: '',
    credit_assessment: '',
    credit_risk: '',
};

const CreateSponsor = ({ sponsor: initialSponsor, isEdit }: CreateSponsorProps) => {
    const { canEdit } = useCurrentUser();
    const [sponsor, setSponsor] = useState<Partial<Sponsor>>(initialSponsor ?? emptySponsor);
    const [dialogOpen, setDialogOpen] = useState(false);
    const navigate = useNavigate();

    const handleChange = (field: string, value: any) => {
        const cleanedValue = value === '' || value === undefined ? null : value;
        setSponsor((prev) => ({ ...prev, [field]: cleanedValue }));
    };

    const handleSubmit = async () => {
        try {
            const response = initialSponsor
                ? await apiPut('sponsor', sponsor.id!, sponsor)
                : await apiPost('sponsors', sponsor);

            if (response && response.success) {
                navigate(initialSponsor ? `/sponsors/${sponsor.id}` : '/sponsors');
            } else {
                console.error('Sponsor save failed:', response?.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Error saving sponsor:', error);
        }
    };

    return (
        <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ position: 'sticky', zIndex: 10, marginBottom: '10px' }}>
                <SimpleToolbar>
                    {initialSponsor && (
                        <CustomButton
                            handleButtonPress={() => navigate(`/sponsors/${sponsor.id}`)}
                            isButtonDisabled={false}
                            solid={false}
                            text="Cancel"
                            data-position="left"
                        />
                    )}
                    <CustomButton
                        handleButtonPress={handleSubmit}
                        isButtonDisabled={!canEdit || !sponsor?.sponsor_name}
                        text={initialSponsor ? 'Update Sponsor' : 'Create Sponsor'}
                        data-position="left"
                    />
                    <ConfirmDialog
                        open={dialogOpen}
                        endpoint="sponsor"
                        id={sponsor.id!}
                        title="Delete Sponsor"
                        description="Are you sure you want to delete this sponsor? This action cannot be undone."
                        confirmText="Delete"
                        cancelText="Cancel"
                        onClose={() => setDialogOpen(false)}
                        onSuccess={() => navigate('/sponsors')}
                    />
                    {isEdit && (
                        <CustomButton
                            handleButtonPress={() => setDialogOpen(true)}
                            isButtonDisabled={false}
                            text="Delete Sponsor"
                            isDeleteButton
                            data-position="right"
                        />
                    )}
                </SimpleToolbar>
            </Box>

            <Box sx={{ maxWidth: '95%', margin: '0 auto', padding: 4 }}>
                <DynamicForm
                    config={sponsorFieldConfig}
                    values={sponsor}
                    onChange={handleChange}
                    numColumns={1}
                    optionsMap={{ sponsorTypes: sponsorTypeFormOptions }}
                />
            </Box>
        </Box>
    );
};

export default CreateSponsor;
